import React from "react";
import { Button } from "Components/Base/Inputs/Button";

/**
 * ButtonUnderlined component
 *
 * A custom button component that renders an underlined text button with optional icon.
 *
 * @param {Function} onClick - The function to be called when the button is clicked
 * @param {string} [color="secondary"] - The color of the button, defaults to "secondary"
 * @param {string} label - The text to be displayed on the button
 * @param {string|React.ReactNode} [icon] - The CSS class name or React element for the icon to be displayed at the end of the button
 * @param {string} [toolTipTitle] - The text to be displayed in the tooltip
 * @param {string|number} [fontWeight] - The font weight of the button text
 * @param {string} [fontSize="14px"] - The font size of the button text
 * @param {boolean} [hoverEffect=true] - Whether to show hover background effect
 * @param {boolean} [noWrap=false] - Whether to prevent text wrapping
 * @param {boolean} [disabled=false] - Whether the button is disabled
 * @param {string} [id] - Custom ID for the button element. If not provided, generates random ID
 * @param {React.ReactNode} [startIcon] - Icon element to display at the start of the button
 * @param {string} [size="small"] - Size of the button. One of: "small", "medium", "large"
 * @returns {React.Element} A Button component with underlined text and optional icon
 */
const ButtonUnderlined = ({
  onClick,
  color = "secondary",
  label,
  startIcon,
  icon,
  toolTipTitle,
  fontWeight,
  fontSize = "14px",
  hoverEffect = true,
  noWrap = false,
  disabled = false,
  id,
  size = "small",
  sx,
}) => {
  const endIcon =
    typeof icon === "string" ? (
      <span className={icon} aria-hidden="true" />
    ) : (
      icon
    );

  return (
    <Button
      id={
        id ||
        `${label ? `${label.replace(/\s+/g, "")}-` : ""}${Math.floor(Math.random() * 10000)}`
      }
      onClick={onClick}
      endIcon={endIcon}
      startIcon={startIcon}
      color={color}
      size={size}
      variant="text"
      toolTipTitle={toolTipTitle}
      disabled={disabled}
      sx={{
        padding: 0,
        justifyContent: "start",
        whiteSpace: noWrap ? "nowrap" : "normal",
        opacity: disabled ? 0.5 : 1,
        "&:hover": {
          backgroundColor: hoverEffect ? undefined : "transparent",
        },
        ...sx,
      }}
    >
      <span
        style={{
          fontSize,
          fontWeight,
          textDecoration: "underline",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {label}
      </span>
    </Button>
  );
};

export default ButtonUnderlined;
